import _ from 'lodash';
import { getNormalizedProjectStatistics, getRequestWeekForUser } from '../../helpers';
import { formatDateToISOString } from '../../helpers/dates';
import {
  ADD_LOG_FULFILLED,
  ADD_OVERTIME_REPORT_FULFILLED,
  ADD_PROJECT_FULFILLED,
  ADD_PROJECT_MONTH_REPORT_STATISTIC_FULFILLED,
  ADD_PROJECT_MONTH_STATISTIC_FULFILLED,
  ADD_USER_FULFILLED,
  BULK_SET_VACATIONS_FULFILLED,
  CHANGE_DATES,
  CHANGE_SELECTED_USER_FULFILLED,
  EDIT_PROJECT_FULFILLED,
  EDIT_USER_FULFILLED,
  PROJECT_SEARCH_FULFILLED,
  REMOVE_LOG_FULFILLED,
  REMOVE_PROJECT_FULFILLED,
  REQUEST_LOGS_FULFILLED,
  UPDATE_LOG_FULFILLED,
} from '../actions/types';
import { CHECK_IN_FULFILLED } from '../actions/work-sessions';
import type { ActionWithPayload, User } from '../../types';

export function selectedUserReducer(state: User | null = null, action: ActionWithPayload): any {
  switch (action.type) {
    case CHANGE_SELECTED_USER_FULFILLED:
      return action.payload;
    default:
      return state;
  }
}

export function datesReducer(state: Array<string> = [], action: ActionWithPayload): Array<string> {
  switch (action.type) {
    case REQUEST_LOGS_FULFILLED:
      return action.payload.logs.map(day => formatDateToISOString(new Date(day.date)));
    case CHANGE_DATES:
      return action.payload;
    default:
      return state;
  }
}

export function daysInWeekReducer(state: Array<any> = [], action: ActionWithPayload): Array<any> {
  switch (action.type) {
    case REQUEST_LOGS_FULFILLED:
      return action.payload.logs;
    default:
      return state;
  }
}

export function currentUserReducers(state: User | null = null, action: ActionWithPayload): any {
  switch (action.type) {
    case EDIT_USER_FULFILLED:
      if (state && state.id === action.payload.id) {
        return { ...state, ...action.payload };
      }

      return state;
    case CHECK_IN_FULFILLED:
      return {
        ...state,
        needsRemoteCheckin: false,
        checkinTime: action.payload.data.checkinTime
      };
    default:
      return state;
  }
}

function addCalendarRequest(state: Record<string, any> | null = null, payload: any) {
  const { logs, selectedUserId } = payload;

  if (!state) {
    return { ...state };
  }

  return {
    ...state,
    calendar: {
      ...state.calendar,
      [getRequestWeekForUser(new Date(logs[0].date), selectedUserId)]: new Date(),
    },
  };
}

function addProjectsRequest(state: Record<string, any> | null = null, year: number, month: number) {
  if (!state) {
    return { ...state };
  }

  return {
    ...state,
    projects: {
      ...state.projects,
      [`${month}-${year}`]: new Date(),
    },
  };
}

export function requestsReducer(state: Record<string, any> | null = null, action: ActionWithPayload): Record<string, any> {
  switch (action.type) {
    case REQUEST_LOGS_FULFILLED:
      return addCalendarRequest(state, action.payload);
    case PROJECT_SEARCH_FULFILLED:
      return addProjectsRequest(state, action.payload.year, action.payload.month);
    default:
      return state || {};
  }
}

function addProjectDropdown(state: Array<any>, newProject: any) {
  return [
    ...state,
    newProject,
  ];
}

function editProjectDropdown(state: Array<any>, editedProject: any) {
  const updatedState = [..._.filter(state, p => p.id !== editedProject.id), editedProject];

  return updatedState;
}

function removeProjectDropdown(state: Array<any>, removedProjectId: number) {
  const remainingprojects = _.filter(state, p => p.id !== removedProjectId);

  return remainingprojects;
}

export function rolesReducer(state: Array<any> = [], action: ActionWithPayload): Array<any> {
  switch (action.type) {
    default:
      return state;
  }
}

export function projectsDropdownReducer(state: Array<any> = [], action: ActionWithPayload): Array<any> {
  switch (action.type) {
    case ADD_PROJECT_FULFILLED:
      return addProjectDropdown(state, action.payload);
    case EDIT_PROJECT_FULFILLED:
      return editProjectDropdown(state, action.payload);
    case REMOVE_PROJECT_FULFILLED:
      return removeProjectDropdown(state, action.payload);
    default:
      return state;
  }
}

function addProjectMonthStatistics(state: Record<string, any>, payload: any) {
  return {
    ...state,
    ...getNormalizedProjectStatistics(payload.stats, payload.month, payload.year),
  };
}

export function projectStatisticReducer(state: any, action: ActionWithPayload): any {
  switch (action.type) {
    case ADD_PROJECT_MONTH_STATISTIC_FULFILLED:
      return addProjectMonthStatistics(state, action.payload);
    case ADD_PROJECT_MONTH_REPORT_STATISTIC_FULFILLED:
      return { ...state, monthReport: action.payload };
    default:
      return state || {};
  }
}

export function projectsWeeklyHoursReducers(state: any, action: ActionWithPayload): any {
  const { payload } = action;

  switch (action.type) {
    case ADD_LOG_FULFILLED: case UPDATE_LOG_FULFILLED: case REMOVE_LOG_FULFILLED:
      return { ...state, ...payload.projects_weekly_hours };
    case REQUEST_LOGS_FULFILLED:
      return { ...state, ...payload.projectsWeeklyHours };
    default:
      return state || [];
  }
}

export function vacationsReducer(state: Record<string, any>, action: ActionWithPayload): Record<string, any> {
  const updateState = (orgState: Record<string, any>, userId: number, payload: any) => ({
    ...orgState,
    [userId.toString()]: payload,
  });

  switch (action.type) {
    case ADD_USER_FULFILLED:
      return updateState(state, action.payload.vacations.id, action.payload.vacations);
    case EDIT_USER_FULFILLED:
      return updateState(state, action.payload.vacations.id, action.payload.vacations);
    case BULK_SET_VACATIONS_FULFILLED:
      return updateState(state, action.payload.id, action.payload);
    default:
      return state || {};
  }
}

export function overtimeReducer(state: any, action: ActionWithPayload): any {
  if (action && action.type === ADD_OVERTIME_REPORT_FULFILLED) {
    return action.payload;
  }

  return state || {};
}
